import { apiFetch } from "./apiFetch";
import { API_BASE_ADMIN as API_BASE, STORAGE_KEYS, isCapacitorRuntime } from "../config/vars";
import { playNotificationSound } from "./notificationSound";

function getPlugins() {
    const cap = window?.Capacitor;
    return {
        PushNotifications: cap?.Plugins?.PushNotifications,
        LocalNotifications: cap?.Plugins?.LocalNotifications,
        Badge: cap?.Plugins?.Badge || cap?.Plugins?.CapacitorBadge,
        FirebaseMessaging: cap?.Plugins?.FirebaseMessaging,
    };
}

function getNativePushPlatform() {
    try {
        const platform = window?.Capacitor?.getPlatform?.();
        return platform === "ios" ? "ios" : "android";
    } catch (_) {
        return "android";
    }
}

function normalizeTokenValue(payload) {
    if (!payload) return "";
    if (typeof payload === "string") return payload.trim();
    return String(payload?.token || payload?.value || "").trim();
}

function storePushToken(tokenValue) {
    try {
        if (tokenValue) {
            localStorage.setItem(STORAGE_KEYS.FCM_TOKEN_ANDROID, tokenValue);
        }
    } catch (_) {
        // ignore
    }
}

const PENDING_PUSH_OPEN_KEY = "WR_ADMIN_PENDING_PUSH_OPEN";

function normalizePushKind(value) {
    const raw = String(value || "").trim().toLowerCase();
    if (!raw) return "";
    if (
        [
            "license",
            "license_renewal",
            "license-renewal",
            "license_request",
            "license_renewal_request",
            "license_renewal_resolved",
        ].includes(raw)
    ) {
        return "license";
    }
    if (["news", "news_item", "announcement"].includes(raw)) return "news";
    if (["poll", "polls", "survey"].includes(raw)) return "poll";
    if (["feedback", "feedbacks"].includes(raw)) return "feedback";
    return raw;
}

function parseJsonObject(value) {
    if (typeof value !== "string") return null;
    const trimmed = value.trim();
    if (!trimmed || (trimmed[0] !== "{" && trimmed[0] !== "[")) return null;
    try {
        const parsed = JSON.parse(trimmed);
        return parsed && typeof parsed === "object" ? parsed : null;
    } catch (_) {
        return null;
    }
}

function parsePushDeepLink(url) {
    if (!url) return null;
    try {
        const parsedUrl = new URL(String(url));
        if (parsedUrl.protocol !== "wra-admin:") return null;
        if (parsedUrl.hostname !== "push") return null;
        return normalizePushPayload({
            kind: parsedUrl.searchParams.get("kind"),
            request_id: parsedUrl.searchParams.get("request_id"),
            network_id: parsedUrl.searchParams.get("network_id"),
            news_id: parsedUrl.searchParams.get("news_id"),
            poll_id: parsedUrl.searchParams.get("poll_id"),
            feedback_id: parsedUrl.searchParams.get("feedback_id"),
        });
    } catch (_) {
        return null;
    }
}

function normalizePushPayload(payload) {
    const source = payload && typeof payload === "object" ? payload : {};
    const pick = (...keys) => {
        for (const key of keys) {
            const value = source?.[key];
            if (value === undefined || value === null) continue;
            if (typeof value === "object") continue;
            const normalized = String(value).trim();
            if (normalized) return normalized;
        }
        return "";
    };

    let kind = normalizePushKind(pick("kind", "type", "notification_kind", "category"));
    const request_id = pick("request_id", "renewal_request_id", "renewalRequestId");
    const network_id = pick("network_id", "networkId");
    const news_id = pick("news_id", "newsId");
    const poll_id = pick("poll_id", "pollId");
    const feedback_id = pick("feedback_id", "feedbackId");

    if (!kind) {
        if (request_id || network_id) kind = "license";
        else if (news_id) kind = "news";
        else if (poll_id) kind = "poll";
        else if (feedback_id) kind = "feedback";
    }

    if (!kind) return null;

    const out = { kind };
    if (request_id) out.request_id = request_id;
    if (network_id) out.network_id = network_id;
    if (news_id) out.news_id = news_id;
    if (poll_id) out.poll_id = poll_id;
    if (feedback_id) out.feedback_id = feedback_id;
    return out;
}

function buildPushDeepLinkUrl(parsed) {
    if (!parsed?.kind) return "";
    const params = new URLSearchParams();
    params.set("kind", parsed.kind);
    Object.entries(parsed).forEach(([key, value]) => {
        if (key === "kind" || value === undefined || value === null) return;
        params.set(key, String(value));
    });
    return `wra-admin://push?${params.toString()}`;
}

function extractNotificationPayload(action) {
    const candidates = [
        action?.notification?.data,
        action?.notification?.extra,
        action?.notification,
        action?.data,
        action?.extra,
        action,
    ];

    for (const candidate of candidates) {
        const parsedCandidate = parseJsonObject(candidate);
        if (parsedCandidate) return parsedCandidate;
        if (candidate && typeof candidate === "object") {
            const nestedPayloads = [candidate.data, candidate.extra, candidate.payload, candidate.notification];
            for (const nested of nestedPayloads) {
                const parsedNested = parseJsonObject(nested);
                if (parsedNested) return parsedNested;
                if (nested && typeof nested === "object") return nested;
            }
            return candidate;
        }
    }
    return {};
}

function persistPendingPushOpen(parsed) {
    if (!parsed) return;
    try {
        localStorage.setItem(PENDING_PUSH_OPEN_KEY, JSON.stringify(parsed));
    } catch (_) {
        // ignore
    }
    try {
        window.dispatchEvent(new Event("wrPushOpen"));
    } catch (_) {
        // ignore
    }
}

function handleNotificationAction(action) {
    const extra = extractNotificationPayload(action);
    const parsedFromLink = parsePushDeepLink(
        extra?.deep_link || extra?.deeplink || extra?.url || extra?.link || extra?.click_action
    );
    const parsedPayload = normalizePushPayload(extra) || parsedFromLink;
    try {
        localStorage.setItem(STORAGE_KEYS.LAST_PUSH, JSON.stringify(extra || {}));
    } catch (_) {
        // ignore
    }
    persistPendingPushOpen(parsedPayload);
}

async function postTokenToBackend(tokenValue) {
    if (!API_BASE) return;
    const token = (tokenValue || "").trim();
    if (!token) return;

    try {
        const lastSent = localStorage.getItem(STORAGE_KEYS.FCM_TOKEN_ANDROID_SENT);
        if (lastSent === token) return;
    } catch (_) {
        // ignore
    }

    const res = await apiFetch(`${API_BASE}/push/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, platform: getNativePushPlatform() }),
    });

    if (res.ok) {
        try {
            localStorage.setItem(STORAGE_KEYS.FCM_TOKEN_ANDROID_SENT, token);
        } catch (_) {
            // ignore
        }
    }
}

export async function registerStoredPushToken() {
    if (!isCapacitorRuntime()) return;
    try {
        const token = localStorage.getItem(STORAGE_KEYS.FCM_TOKEN_ANDROID);
        if (token) {
            await postTokenToBackend(token);
        }
    } catch (_) {
        // ignore
    }
}

export function consumePendingAdminPushOpen() {
    let raw = null;
    try {
        raw = localStorage.getItem(PENDING_PUSH_OPEN_KEY);
    } catch (_) {
        return null;
    }
    if (!raw) return null;
    try {
        localStorage.removeItem(PENDING_PUSH_OPEN_KEY);
    } catch (_) {
        // ignore
    }
    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : null;
    } catch (_) {
        return null;
    }
}

export async function initPushNotifications() {
    if (!isCapacitorRuntime()) return;

    const { PushNotifications, LocalNotifications, Badge, FirebaseMessaging } = getPlugins();
    if (!PushNotifications) {
        console.warn("[Push] PushNotifications plugin not found");
        return;
    }

    try {
        if (LocalNotifications?.createChannel) {
            await LocalNotifications.createChannel({
                id: "wr_alerts_v2",
                name: "Сповіщення",
                description: "WorkRestart admin notifications",
                importance: 5,
                sound: "default",
                visibility: 1,
                lights: true,
                vibration: true,
            });
            // Keep native MessagingService channel in sync for background tray + sound.
            await LocalNotifications.createChannel({
                id: "default",
                name: "Default",
                description: "WorkRestart notifications",
                importance: 5,
                sound: "default",
                visibility: 1,
                lights: true,
                vibration: true,
            });
        }
    } catch (_) {
        // ignore
    }

    try {
        try {
            if (LocalNotifications?.checkPermissions && LocalNotifications?.requestPermissions) {
                const lp = await LocalNotifications.checkPermissions();
                if (lp?.display !== "granted") {
                    await LocalNotifications.requestPermissions();
                }
            }
        } catch (_) {
            // ignore
        }

        const perm = await PushNotifications.checkPermissions();
        if (perm?.receive !== "granted") {
            const req = await PushNotifications.requestPermissions();
            if (req?.receive !== "granted") {
                console.warn("[Push] notification permission not granted");
            }
        }

        PushNotifications.addListener("registration", async (t) => {
            const tokenValue = normalizeTokenValue(t);
            storePushToken(tokenValue);
            await postTokenToBackend(tokenValue);
        });

        if (FirebaseMessaging?.addListener) {
            FirebaseMessaging.addListener("tokenReceived", async (event) => {
                const tokenValue = normalizeTokenValue(event);
                if (!tokenValue) return;
                storePushToken(tokenValue);
                await postTokenToBackend(tokenValue);
            });
        }

        PushNotifications.addListener("registrationError", (err) => {
            console.warn("[Push] registrationError", err);
        });

        PushNotifications.addListener("pushNotificationReceived", async (n) => {
            try {
                const extra = n?.data || {};
                const title = n?.title || extra?.title || "WorkRestart Admin";
                const body = n?.body || extra?.body || "";
                const parsedPayload = normalizePushPayload(extra);
                const extraWithLink = { ...extra };
                const deepLink = buildPushDeepLinkUrl(parsedPayload);
                if (deepLink) {
                    extraWithLink.deep_link = deepLink;
                }

                try {
                    playNotificationSound();
                    window.dispatchEvent(new CustomEvent("wrPushReceived", { detail: extraWithLink }));
                } catch (_) {
                    // ignore
                }

                if (LocalNotifications?.schedule) {
                    await LocalNotifications.schedule({
                        notifications: [
                            {
                                id: Math.floor(Date.now() / 1000),
                                title,
                                body,
                                extra: extraWithLink,
                                channelId: "wr_alerts_v2",
                                schedule: { at: new Date(Date.now() + 250) },
                                sound: "default",
                            },
                        ],
                    });
                }

                try {
                    if (Badge?.increase) {
                        await Badge.increase();
                    } else if (Badge?.get && Badge?.set) {
                        const cur = await Badge.get();
                        const next = Math.max(0, Number(cur?.count) || 0) + 1;
                        await Badge.set({ count: next });
                    } else if (Badge?.set) {
                        await Badge.set({ count: 1 });
                    }
                } catch (_) {
                    // ignore
                }
            } catch (_) {
                // ignore
            }
        });

        PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
            handleNotificationAction(action);
        });

        if (LocalNotifications?.addListener) {
            LocalNotifications.addListener("localNotificationActionPerformed", (action) => {
                handleNotificationAction(action);
            });
        }

        await PushNotifications.register();
        await registerStoredPushToken();
    } catch (e) {
        console.warn("[Push] init failed", e);
    }
}
